// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// The tour is won on merits (the per-round ranking points shown in the results
// table), not on the raw game points the per-hole series carries. This module
// turns both into a position-per-hole so the standings graph moves hole by hole
// but lands on exactly the merit standing the table shows.
//
// Inside a round the merits are provisional: the round's merit ladder is dealt
// out by the running points in that round, so a player climbing the round climbs
// the graph. At the round's last hole the provisional deal is replaced by the
// merits the round actually awarded, and the final hole of the tour is pinned to
// the authoritative merit totals (which also carry tour-long games such as
// Eclectic, since those have no per-hole history to walk).

import { UNKNOWN_HANDICAP } from '@/features/leaderboard/tieBreak';

export type RoundMerits = { roundId: string; meritsByPlayerId: Record<string, number> };

export type MeritPositionsInput = {
  /** Player ids, already normalized to the spelling used in every map below. */
  playerIds: string[];
  /** Per player: cumulative tour points at each hole index, carried over holes not yet played. */
  cumulativePoints: Record<string, number[]>;
  /** Round id owning each hole index, in graph axis order. */
  roundIdByHole: string[];
  meritsByRound: RoundMerits[];
  /** Authoritative merit total per player, used to pin the last hole. */
  totalMerits?: Record<string, number>;
  /** Hole indexes actually plotted. */
  sampleIndexes: number[];
  /**
   * Per player: the handicap that separates players level on merits, lowest first. Absent
   * players cannot be separated and keep sharing a place, as before.
   */
  tieBreakHandicaps?: Record<string, number>;
};

export type MeritPositions = {
  /** Per player: merit total at each sampled hole. */
  meritsBySample: Record<string, number[]>;
  /** Per player: leaderboard position at each sampled hole (standard competition ranking). */
  positionsBySample: Record<string, number[]>;
  /** Per player: merit total at the end of the tour. */
  totals: Record<string, number>;
};

const sum = (values: number[]) => values.reduce((total, value) => total + value, 0);

// Hand out `ladder` (merit values, best first) to `participants` ranked by their
// points in the round. Players level on points split the slots they jointly
// occupy, which is how a shared place ends up as e.g. 3.5 merits.
const dealLadder = (ladder: number[], pointsByPlayer: Record<string, number>, participants: string[]) => {
  const dealt: Record<string, number> = {};
  if (participants.length === 0) return dealt;
  const ranked = participants
    .slice()
    .sort((a, b) => (pointsByPlayer[b] ?? 0) - (pointsByPlayer[a] ?? 0) || a.localeCompare(b));
  let index = 0;
  while (index < ranked.length) {
    let last = index;
    while (last + 1 < ranked.length && (pointsByPlayer[ranked[last + 1]] ?? 0) === (pointsByPlayer[ranked[index]] ?? 0)) {
      last += 1;
    }
    const groupSize = last - index + 1;
    const share = sum(ladder.slice(index, last + 1)) / groupSize;
    for (let position = index; position <= last; position += 1) {
      dealt[ranked[position]] = share;
    }
    index = last + 1;
  }
  return dealt;
};

export const buildMeritPositions = ({
  playerIds,
  cumulativePoints,
  roundIdByHole,
  meritsByRound,
  totalMerits,
  sampleIndexes,
  tieBreakHandicaps,
}: MeritPositionsInput): MeritPositions => {
  const handicapOf = (playerId: string) => tieBreakHandicaps?.[playerId] ?? UNKNOWN_HANDICAP;
  const roundOrder: string[] = [];
  const ranges = new Map<string, { start: number; end: number }>();
  roundIdByHole.forEach((roundId, index) => {
    if (!roundId) return;
    const existing = ranges.get(roundId);
    if (existing) {
      existing.end = index;
      return;
    }
    ranges.set(roundId, { start: index, end: index });
    roundOrder.push(roundId);
  });

  const meritsByRoundId = new Map(meritsByRound.map((entry) => [entry.roundId, entry.meritsByPlayerId]));

  // A round is resolved once its merits exist; until then it borrows the most
  // recent resolved round's ladder so the round in progress still shows movement
  // at a plausible magnitude.
  const ladderByRound = new Map<string, number[]>();
  const resolvedRounds = new Set<string>();
  let previousLadder: number[] = [];
  roundOrder.forEach((roundId) => {
    const merits = meritsByRoundId.get(roundId);
    const values = merits ? Object.values(merits) : [];
    if (values.length > 0) {
      resolvedRounds.add(roundId);
      previousLadder = values.slice().sort((a, b) => b - a);
    }
    ladderByRound.set(roundId, previousLadder);
  });

  // Merits banked before each round tees off.
  const meritsBeforeRound = new Map<string, Record<string, number>>();
  const banked: Record<string, number> = {};
  playerIds.forEach((playerId) => {
    banked[playerId] = 0;
  });
  roundOrder.forEach((roundId) => {
    meritsBeforeRound.set(roundId, { ...banked });
    const merits = meritsByRoundId.get(roundId);
    if (!merits) return;
    playerIds.forEach((playerId) => {
      banked[playerId] += merits[playerId] ?? 0;
    });
  });

  const totals: Record<string, number> = {};
  playerIds.forEach((playerId) => {
    totals[playerId] = totalMerits?.[playerId] ?? banked[playerId] ?? 0;
  });

  const meritsBySample: Record<string, number[]> = {};
  const positionsBySample: Record<string, number[]> = {};
  playerIds.forEach((playerId) => {
    meritsBySample[playerId] = [];
    positionsBySample[playerId] = [];
  });

  const lastSampleIndex = sampleIndexes[sampleIndexes.length - 1];

  sampleIndexes.forEach((holeIndex) => {
    // Holes with no round (padding at the tail) inherit the last round seen.
    let roundId = roundIdByHole[holeIndex] ?? '';
    if (!roundId) {
      for (let index = Math.min(holeIndex, roundIdByHole.length - 1); index >= 0; index -= 1) {
        if (roundIdByHole[index]) {
          roundId = roundIdByHole[index];
          break;
        }
      }
    }
    const range = ranges.get(roundId);
    const base = meritsBeforeRound.get(roundId) ?? banked;
    const roundStart = range ? range.start : 0;

    const roundPoints: Record<string, number> = {};
    playerIds.forEach((playerId) => {
      const series = cumulativePoints[playerId] ?? [];
      const atHole = series[holeIndex] ?? series[series.length - 1] ?? 0;
      const atStart = roundStart > 0 ? series[roundStart - 1] ?? 0 : 0;
      roundPoints[playerId] = atHole - atStart;
    });

    const resolved = resolvedRounds.has(roundId);
    const roundMerits = meritsByRoundId.get(roundId);
    const atRoundEnd = !!range && holeIndex >= range.end;

    let earned: Record<string, number>;
    if (resolved && atRoundEnd && roundMerits) {
      earned = roundMerits;
    } else {
      const participants = resolved && roundMerits
        ? playerIds.filter((playerId) => roundMerits[playerId] !== undefined)
        : playerIds.filter((playerId) => roundPoints[playerId] !== 0);
      earned = dealLadder(ladderByRound.get(roundId) ?? [], roundPoints, participants);
    }

    const merits: Record<string, number> = {};
    playerIds.forEach((playerId) => {
      merits[playerId] = (base[playerId] ?? 0) + (earned[playerId] ?? 0);
    });

    // The last plotted hole is the standing, so use the authoritative totals
    // rather than whatever the per-hole walk accumulated.
    if (holeIndex === lastSampleIndex && totalMerits) {
      playerIds.forEach((playerId) => {
        merits[playerId] = totals[playerId];
      });
    }

    playerIds.forEach((playerId) => {
      // Merits are the standing. Points only break ties while the round's merits are
      // still provisional; letting them break a resolved tie would contradict the
      // results table. Once merits are level and settled, the lower handicap is ahead —
      // the same rule the table ranks on.
      const useTiebreak = !resolved && holeIndex !== lastSampleIndex;
      const ahead = playerIds.filter((other) => {
        if (other === playerId) return false;
        if (merits[other] !== merits[playerId]) return merits[other] > merits[playerId];
        if (useTiebreak && roundPoints[other] !== roundPoints[playerId]) {
          return roundPoints[other] > roundPoints[playerId];
        }
        return handicapOf(other) < handicapOf(playerId);
      }).length;
      meritsBySample[playerId].push(merits[playerId]);
      positionsBySample[playerId].push(ahead + 1);
    });
  });

  return { meritsBySample, positionsBySample, totals };
};
